import React from "react";
import "./ParameterForm.css";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import {SomeJSONSchema} from "ajv/dist/types/json-schema";
import {SubmitHandler, useForm} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {
  ParameterFormCheckbox,
  ParameterFormDropDown,
  ParameterFormNumberInput,
  ParameterFormTextInput,
} from "./ParameterFormInputs";
import LoadingSpinner from "../../../components/LoadingSpinner";
import InfoBubble from "../../../components/InfoBubble";

/** Searches the schema for properties with default values and formats them as required by react hook form */
const extractDefaultValues = (schema: SomeJSONSchema) => {
  const defaultValues: Record<string, string | number | boolean | undefined> =
    {};
  for (const property in schema.properties) {
    const field = schema.properties[property];
    if (field && "default" in field)
      defaultValues[property] =
        "enum" in field
          ? {
              value: field["default"],
              label: "" + field["default"],
            }
          : field["default"];
  }
  return defaultValues;
};

/** Searches the submission for dropdown entries and extracts only the value */
const cleanupDropdowns = (submission: Record<string, unknown>) => {
  for (const property in submission) {
    const value = submission[property];
    if (
      value &&
      typeof value === "object" &&
      "value" in value &&
      "label" in value
    )
      submission[property] = value.value;
  }
};

type ParameterFormProps = {
  /** The schema to build a form from */
  schema: SomeJSONSchema;
  /** A function to execute after the user submits a valid form */
  onValidSubmission: (submission: object) => void;
  /** Whether the form is currently loading due to an ongoing installation */
  loading?: boolean;
};

/** Builds a form from a JSON schema, including form validation */
function ParameterForm({
  schema,
  onValidSubmission,
  loading,
}: ParameterFormProps) {
  const {formatMessage} = useIntl();

  delete schema.$schema;
  const defaultValues = extractDefaultValues(schema);
  const ajv = new Ajv({allErrors: true, allowUnionTypes: true});
  addFormats(ajv);
  const validate = ajv.compile(schema);

  const {
    control,
    register,
    handleSubmit,
    formState: {errors, dirtyFields, isSubmitting, isLoading},
    setError,
  } = useForm<SomeJSONSchema["properties"]>({
    mode: "onChange",
    defaultValues,
  });

  const onSubmit: SubmitHandler<SomeJSONSchema["properties"]> = (
    submission: Record<string, unknown>,
  ) => {
    cleanupDropdowns(submission);
    if (!validate(submission)) {
      validate.errors?.forEach((e) => {
        const fieldName = e.instancePath.substring(1);
        if (schema.properties[fieldName]) {
          setError(fieldName, {
            message: formatMessage({
              id: `appInstallationParameters.ParameterForm.error.invalidInput`,
            }),
          });
        } else {
          setError("root.schema", {
            message: formatMessage({
              id: `appInstallationParameters.ParameterForm.error.invalidForm`,
            }),
          });
        }
      });
    } else onValidSubmission(submission);
  };

  const disabled =
    isSubmitting || isLoading || loading === undefined || loading;
  return (
    <form className="parameterForm" onSubmit={handleSubmit(onSubmit)}>
      {Object.keys(schema.properties).map((fieldName) => {
        const field = schema.properties[fieldName];
        const required = schema.required.includes(fieldName)
          ? {
              value: true,
              message: formatMessage({
                id: "appInstallationParameters.ParameterForm.error.required",
              }),
            }
          : undefined;

        const parameterFormInputProps = {
          fieldName,
          control,
          register,
          field,
          required,
          errors,
          defaultValues,
          disabled,
          dirtyFields,
        };
        return (
          <div key={fieldName}>
            {field.description && (
              <InfoBubble
                idBase="appInstallationParameters.ParameterForm.infoBubble"
                text={field.description}
              >
                <span className="parameterForm__descriptionInfoBubble"></span>
              </InfoBubble>
            )}
            {"enum" in field ? (
              <ParameterFormDropDown {...parameterFormInputProps} />
            ) : field.type === "string" ? (
              <ParameterFormTextInput {...parameterFormInputProps} />
            ) : field.type === "boolean" ? (
              <ParameterFormCheckbox {...parameterFormInputProps} />
            ) : field.type === "number" || field.type === "integer" ? (
              <ParameterFormNumberInput {...parameterFormInputProps} />
            ) : (
              <></>
            )}
          </div>
        );
      })}
      {errors.root?.schema?.message && (
        <span className="parameterForm__error">
          {errors.root?.schema.message}
        </span>
      )}
      <button
        id="submit_button"
        type="submit"
        disabled={disabled}
        className="parameterForm__submit linkButton"
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <FormattedMessage id="general.button.label.install" />
        )}
      </button>
    </form>
  );
}

export default ParameterForm;
